/**
 * Auto-logout reason plumbing between `useSessionTimeout` and the sign-in page.
 *
 * We intentionally keep a module-level cache in addition to sessionStorage so
 * the value survives React StrictMode's double-mount in dev: the first mount
 * would otherwise consume the sessionStorage entry, leaving the "real" second
 * mount with nothing to display.
 *
 * sessionStorage is still used as a fallback so the reason survives a full
 * page reload (e.g. the hook force-reloads the app on logout).
 */

const STORAGE_KEY = 'clinlexis_auto_logout_reason';

export type AutoLogoutReason = 'inactivity' | 'absolute';

let cached: AutoLogoutReason | null | undefined;

export function setAutoLogoutReason(reason: AutoLogoutReason): void {
  cached = reason;
  try {
    sessionStorage.setItem(STORAGE_KEY, reason);
  } catch {
    /* sessionStorage unavailable — in-memory cache still works */
  }
}

/**
 * Reads the current auto-logout reason. First call after a fresh auto-logout
 * pulls from sessionStorage; subsequent calls return the cached value so
 * StrictMode remounts see the same reason.
 */
export function readAutoLogoutReason(): AutoLogoutReason | null {
  if (cached !== undefined) return cached;
  try {
    const value = sessionStorage.getItem(STORAGE_KEY) as AutoLogoutReason | null;
    cached = value;
    return value;
  } catch {
    cached = null;
    return null;
  }
}

/**
 * Clears the cached reason and the sessionStorage entry. Call this after the
 * user has acknowledged the banner (e.g. on successful sign-in) so a future
 * auto-logout in the same SPA session can display its own reason.
 */
export function clearAutoLogoutReason(): void {
  cached = undefined;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
